import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import axios from 'axios';
import API_BASE from '../apiBase';
import PhoneInput from '../components/PhoneInput';
import { getStoredUser, setStoredUser } from '../utils/userStorage';
import { sanitizeProfileForm, validateProfileForm, isValidProfileName, isValidProfilePhone } from '../utils/profileValidation';
import { trackButtonClick, trackFormSubmit } from '../utils/gtm';

function CompleteProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const initialUserState = useMemo(() => {
    return location.state?.user || getStoredUser() || null;
  }, [location.state]);

  const [user, setUser] = useState(initialUserState);
  const [formData, setFormData] = useState({
    name: initialUserState?.name || '',
    company_name: initialUserState?.company_name || '',
    phone: initialUserState?.phone || '',
    phone_country_code: initialUserState?.phone_country_code || '+1',
    address: {
      street1: initialUserState?.address?.street1 || '',
      street2: initialUserState?.address?.street2 || '',
      city: initialUserState?.address?.city || '',
      state: initialUserState?.address?.state || '',
      postal_code: initialUserState?.address?.postal_code || '',
      country: initialUserState?.address?.country || ''
    }
  });

  useEffect(() => {
    let isMounted = true;
    const fetchLatestUser = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/users/me`);
        if (!data) {
          navigate('/login');
          return;
        }
        if (!isMounted) return;
        setUser(data);
        setStoredUser(data);
        setFormData({
          name: data?.name || '',
          company_name: data?.company_name || '',
          phone: data?.phone || '',
          phone_country_code: data?.phone_country_code || '+1',
          address: {
            street1: data?.address?.street1 || '',
            street2: data?.address?.street2 || '',
            city: data?.address?.city || '',
            state: data?.address?.state || '',
            postal_code: data?.address?.postal_code || '',
            country: data?.address?.country || ''
          }
        });
      } catch (err) {
        console.error('Failed to load user for profile completion', err);
        navigate('/login');
      }
    };

    fetchLatestUser();
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateForm = () => {
    const sanitized = sanitizeProfileForm(formData);
    const validationErrors = validateProfileForm(sanitized);
    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      return false;
    }
    if (!formData.phone_country_code) {
      setError('Please select a country code');
      return false;
    }
    if (!sanitized.address.street1 || !sanitized.address.city || !sanitized.address.state || !sanitized.address.country) {
      setError('Please fill in all required address fields');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackFormSubmit('complete_profile', 'complete_profile_page', false);
    setLoading(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    if (!consentChecked) {
      setError('You must agree to the Compliance Policy to continue');
      setLoading(false);
      return;
    }

    try {
      const sanitized = sanitizeProfileForm(formData);
      const updateData = {
        name: sanitized.name,
        company_name: sanitized.company_name,
        phone: sanitized.phone,
        phone_country_code: formData.phone_country_code,
        address: sanitized.address
      };

      const response = await axios.put(`${API_BASE}/api/users/${user.id}`, updateData);
      
      setSuccess('Profile updated successfully!');
      
      // Store updated user data
      setStoredUser(response.data);

      // Upsert compliance policy for SSO flow
      try {
        await axios.put(`${API_BASE}/api/compliance/policy`, {
          data_region: formData.address?.country || '',
          retention_basis: 'payment basis',
          lawful_basis: 'contract',
          consent_required: true,
          consent_source_default: 'user_signup_checkbox',
          purposes: ['code review automation', 'fix issues'],
          pii_categories: ['email', 'username'],
          dsr_contact: user?.email,
          anonymize_on_export: true,
        });
      } catch (_) {}
      
      trackFormSubmit('complete_profile', 'complete_profile_page', true);
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (err) {
      console.error('Profile update error:', err);
      trackFormSubmit('complete_profile', 'complete_profile_page', false, { error: err?.message });
      setError('Failed to update profile: ' + (err?.response?.data?.error || err?.response?.data?.details || err.message));
    }
    
    setLoading(false);
  };



  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", color: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Container component="main" maxWidth="md" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Card sx={{ mt: 0, p: { xs: 2, sm: 4 }, bgcolor: "#fff", borderRadius: 2, boxShadow: '0 20px 40px rgba(40, 34, 34, 0.1)', minWidth: 400, maxWidth: 600, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0 }}>
            <Box
              component="img"
              sx={{ height: 80, mb: 2, mt: 2 }}
              alt="Logo"
              src="/assets/logo.png"
            />
            <Typography component="h1" variant="h5" sx={{ mb: 1, color: "#000", fontWeight: 700 }}>
              Complete Your Profile
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#666', textAlign: 'center' }}>
              Please provide your contact and address information to complete your account setup
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} autoComplete="on" sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#000', fontWeight: 600 }}>
                Contact Information
              </Typography>
              
              <TextField
                margin="normal"
                required
                fullWidth
                label="Full Name"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={loading}
                autoComplete="name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { 
                      borderColor: formData.name ? 
                        (isValidProfileName(formData.name) ? '#4caf50' : '#f44336') : '#e0e0e0' 
                    },
                    '&:hover fieldset': { borderColor: '#bdbdbd' },
                    '&.Mui-focused fieldset': { borderColor: '#000' },
                    borderRadius: 1,
                    bgcolor: '#fff',
                    fontSize: 14,
                    height: '50px'
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 1.5,
                    fontSize: 14,
                    height: '50px'
                  },
                  mb: 1
                }}
              />
              {formData.name && !isValidProfileName(formData.name) && (
                <Typography variant="caption" sx={{ 
                  color: '#f44336',
                  fontWeight: 'bold',
                  display: 'block',
                  mb: 2
                }}>
                  ❌ Please enter a valid name (4-50 characters, letters, spaces, hyphens, apostrophes, and periods only)
                </Typography>
              )}

              <TextField
                margin="normal"
                fullWidth
                label="Company Name (Optional)"
                placeholder="Enter your company name"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                disabled={loading}
                autoComplete="organization"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#bdbdbd' },
                    '&.Mui-focused fieldset': { borderColor: '#000' },
                    borderRadius: 1,
                    bgcolor: '#fff',
                    fontSize: 14,
                    height: '50px'
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 1.5,
                    fontSize: 14,
                    height: '50px'
                  },
                  mb: 2
                }}
              />

              <PhoneInput
                value={formData.phone}
                countryCode={formData.phone_country_code}
                onChange={(value) => handleInputChange('phone', value)}
                onCountryCodeChange={(value) => handleInputChange('phone_country_code', value)}
                label="Phone Number"
                required
                disabled={loading}
                error={formData.phone && !isValidProfilePhone(formData.phone)}
                helperText={formData.phone && !isValidProfilePhone(formData.phone) ? 'Please enter at least 5 digits' : ''}
                autoComplete="tel"
                sx={{ mb: 3 }}
              />

              <Typography variant="h6" sx={{ mb: 3, color: '#000', fontWeight: 600 }}>
                Address Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Street Address 1"
                    value={formData.address.street1}
                    onChange={(e) => handleInputChange('address.street1', e.target.value)}
                    disabled={loading}
                    autoComplete="street-address"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff',
                        fontSize: 14,
                        height: '50px'
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 1.5,
                        fontSize: 14,
                        height: '50px'
                      },
                      mb: 2
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address 2 (Optional)"
                    value={formData.address.street2}
                    onChange={(e) => handleInputChange('address.street2', e.target.value)}
                    disabled={loading}
                    autoComplete="address-line2"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff',
                        fontSize: 14,
                        height: '50px'
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 1.5,
                        fontSize: 14,
                        height: '50px'
                      },
                      mb: 2
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="City"
                    value={formData.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    disabled={loading}
                    autoComplete="address-level2"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff',
                        fontSize: 14,
                        height: '50px'
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 1.5,
                        fontSize: 14,
                        height: '50px'
                      },
                      mb: 2
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="State/Province"
                    value={formData.address.state}
                    onChange={(e) => handleInputChange('address.state', e.target.value)}
                    disabled={loading}
                    autoComplete="address-level1"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff',
                        fontSize: 14,
                        height: '50px'
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 1.5,
                        fontSize: 14,
                        height: '50px'
                      },
                      mb: 2
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    value={formData.address.postal_code}
                    onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                    disabled={loading}
                    autoComplete="postal-code"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff',
                        fontSize: 14,
                        height: '50px'
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 1.5,
                        fontSize: 14,
                        height: '50px'
                      },
                      mb: 2
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Country"
                    value={formData.address.country}
                    onChange={(e) => handleInputChange('address.country', e.target.value)}
                    disabled={loading}
                    autoComplete="country-name"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff',
                        fontSize: 14,
                        height: '50px'
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 1.5,
                        fontSize: 14,
                        height: '50px'
                      },
                      mb: 2
                    }}
                  />
                </Grid>
              </Grid>


              <Box sx={{ mt: 2, color: '#000' }}>
                <FormControlLabel
                  control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} sx={{ '&.Mui-checked': { color: '#000' } }} />}
                  label={<Typography variant="body2" sx={{ color: '#666' }}>I agree to the <Link 
                    href="/compliance-policy" 
                    target="_blank" 
                    rel="noopener" 
                    onClick={() => trackButtonClick('compliance_policy_link', 'complete_profile_page')}
                    sx={{ color: '#000', fontWeight: 600 }}>Compliance Policy</Link></Typography>}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  onClick={() => trackButtonClick('complete_profile_submit', 'complete_profile_page')}
                  sx={{ 
                    py: 1.5, 
                    bgcolor: '#000', 
                    color: '#fff', 
                    borderRadius: 1, 
                    fontWeight: 600, 
                    fontSize: 16,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#333' },
                    '&:disabled': { bgcolor: '#ccc' }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Complete Profile'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default CompleteProfile; 